import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { ClockAfternoon, WarningCircle } from '@phosphor-icons/react'
import { DateTime } from 'luxon'
import { Helmet } from 'react-helmet'
import { useLocation, useNavigate } from 'react-router-dom'

// Context
import useSessionStore from 'Context/useSessionStore'
import ContextTutorial from 'Context/tutorialContext'

// Hooks
import { useSchedule, useUser, useTimeEntries, useCalendarEvents } from 'Hooks'

// Components
import TimeTrackerModal from './Components/TimeTrackerModal'
import { ModalSuccess } from 'Organisms/ModalSuccess'
import { Loading } from 'Organisms/Loading'
import { DaysOfWeek } from './Components/DaysOfWeek'
import FilterTrackerByDate from './Components/FilterTrackerByDate/FilterTrackerByDate'
import { BtnSubmitTracker } from './Components/BtnSubmitTracker'
import ListTimeTicket from './Components/ListTimeTicket/ListTimeTicket'
import { CalendarEventsDrawer } from './Components/CalendarEventsDrawer'
import { ModalDeleteTracker } from './Components'
import { WeeklyTotalTracker } from './Components/WeeklyTotalTracker'
import { TYPE_SUBMIT_ACTION } from './Components/BtnSubmitTracker/BtnSubmitTracker'
import { OnboardingTooltip } from 'Organisms/Onboarding'

// Const and utils
import { Mixpanel } from 'Utils/Mixpanel'
import { getValuePerFilter } from 'Utils/helperFunctions'
import { ROLE_DEVELOPER_NAME } from 'constant/roles'
import { DAYS_OF_WEEK, ONBOARDING_STEPS_TRACKER, PARAMS_URL_TRACKER, STATUS_SUBMIT_HOURS } from './utils/const'
import { getQueryParamsTrakcer, getStatusTracker } from './utils/helpers'

const TimeTracker = ({ onApprovalChange }) => {
  const { user } = useSessionStore((state) => ({ user: state.user }))

  const { openTutorialContext } = useContext(ContextTutorial)

  const location = useLocation()
  const navigate = useNavigate()
  const { queryParams, startDateParamsQuery, currentDateQuery } = getQueryParamsTrakcer({ location })

  const isDeveloper = user.role === ROLE_DEVELOPER_NAME

  const [approval, setApproval] = useState(STATUS_SUBMIT_HOURS.UNSUBMITTED)
  const [activeDate, setActiveDate] = useState(
    startDateParamsQuery
      ? DateTime.fromISO(startDateParamsQuery).setZone('local')
      : currentDateQuery
        ? DateTime.fromISO(currentDateQuery).setZone('local')
        : DateTime.now()
  )
  const [activeWeek, setActiveWeek] = useState([])
  const [visible, setVisible] = useState(false)
  const [timeEntriesPerDay, setTimeEntriesPerDay] = useState([])
  const [weekCalendarEvents, setWeekCalendarEvents] = useState([])
  const [calendarEventsPerDay, setCalendarEventsPerDay] = useState([])
  const [sourceFilters, setSourceFilters] = useState([])
  const [calendarDrawerOpen, setCalendarDrawerOpen] = useState(false)
  const [usersFilter, setUsersFilter] = useState([])
  const [selectedTracker, setSelectedTracker] = useState(null)
  const [alertTracker, setAlertTracker] = useState(false)
  const [modalDeleteTracker, setModalDeleteTracker] = useState(false)
  const [modalDetailsApproval, setModalDetailsApproval] = useState({
    open: false
  })
  const [errorTracker, setErrorTracker] = useState('')

  const currentDayName = activeDate.toFormat('cccc')

  // Hooks
  const {
    listTimeEntries,
    loadingTimeEntriesHook,
    getTimeEntriesHook,
    submitHoursTimeEntriesHook,
    reSubmitHoursTimeEntriesHook,
    activeTimerTimeEntriesHook,
    desActiveTimerTimeEntriesHook
  } = useTimeEntries()
  const { getAllUsersByFilterHook } = useUser()
  const { scheduleHook, getScheduleHook } = useSchedule()
  const {
    connectedCalendarsHook,
    getConnectedCalendarsHook,
    getGoogleEventsHook,
    includeEventHook,
    excludeEventHook
  } = useCalendarEvents()

  const handleChangeDay = async ({ days, now, date }) => {
    let updateDay

    // If it is now it is to return today
    if (now) {
      updateDay = DateTime.now()
    } else if (date) {
      updateDay = date
    } else {
      updateDay = activeDate.plus({ days })
    }

    const daysOfActiveWeek = activeWeek.map((item) => item.toFormat('dd'))

    setActiveDate(updateDay)

    // update param date by uri
    queryParams.set(PARAMS_URL_TRACKER.CURRENT_DATE, updateDay.toFormat('yyyy-MM-dd'))
    queryParams.delete(PARAMS_URL_TRACKER.DATE)
    queryParams.delete(PARAMS_URL_TRACKER.IS_TRACKER)
    navigate(`?${queryParams.toString()}`, { replace: true })

    if (errorTracker) setErrorTracker('')

    // If the current day is not included in the days of the week, we search for new data.
    if (daysOfActiveWeek.includes(updateDay.toFormat('dd'))) {
      return handleChangeListPerDay({ listEntries: listTimeEntries, currentDay: updateDay })
    }

    // only search old data
    await handleSetWeek({ currentDay: updateDay })
  }

  const handleSetWeek = async ({ currentDay }) => {
    let startOfWeek
    const weekDates = []

    if ([DAYS_OF_WEEK.SATURDAY, DAYS_OF_WEEK.SUNDAY].includes(currentDay.toFormat('cccc'))) {
      startOfWeek = currentDay.plus({ days: 3 }).startOf('week')
    } else {
      startOfWeek = currentDay.startOf('week')
    }

    for (let i = -2; i < 5; i++) {
      weekDates.push(startOfWeek.plus({ days: i }))
    }

    setActiveWeek(weekDates)
    await handleSetWeekTimeEntries({ activeWeek: weekDates, currentDay, filterUser: usersFilter })
  }

  const handleSetWeekTimeEntries = async ({ activeWeek, currentDay, filterUser } = {}) => {
    if (activeWeek.length === 0) return

    const startDate = activeWeek[0].startOf('day')
    const endDate = activeWeek[6].endOf('day')

    const listEntries = await getTimeEntriesHook({
      startDate,
      endDate,
      users: getValuePerFilter({ list: filterUser }),
      tracker: true,
      order: 'asc',
      page: 1,
      limit: 10000
    })

    const calendarEvents = connectedCalendarsHook.length > 0
      ? await getGoogleEventsHook({ startDate, endDate })
      : []

    setWeekCalendarEvents(calendarEvents)
    handleChangeListPerDay({ listEntries, calendarEvents, currentDay })
  }

  const handleChangeListPerDay = ({ listEntries, calendarEvents = weekCalendarEvents, currentDay }) => {
    const timeEntriesByToDay = listEntries.filter(
      (item) => DateTime.fromISO(item.spent_date).setZone('local').toISODate() === currentDay.toFormat('yyyy-MM-dd')
    )

    const calendarEventsByToDay = calendarEvents.filter(
      (event) => DateTime.fromISO(event.start_at).setZone('local').toISODate() === currentDay.toFormat('yyyy-MM-dd')
    )

    setApproval(getStatusTracker({ listTimeEntries: timeEntriesByToDay }))

    setTimeEntriesPerDay(timeEntriesByToDay)
    setCalendarEventsPerDay(calendarEventsByToDay)
  }

  const handleRefreshTraker = async () => {
    await handleSetWeekTimeEntries({ activeWeek, currentDay: activeDate, filterUser: usersFilter })
  }

  const handleChangeSwitches = async ({ values } = {}) => {
    setUsersFilter(values)
    handleSetWeekTimeEntries({ activeWeek, currentDay: activeDate, filterUser: values })
  }

  const handleChangeSourceFilters = ({ values } = {}) => setSourceFilters(values)

  const handleIncludeEvent = async ({ eventId }) => {
    const res = await includeEventHook({ eventId })

    if (!res.success) return

    Mixpanel.track('Time Tracker - Include calendar event in timesheet')
    await handleRefreshTraker()
  }

  const handleExcludeEvent = async ({ eventId }) => {
    const res = await excludeEventHook({ eventId })

    if (!res.success) return

    Mixpanel.track('Time Tracker - Remove calendar event from timesheet')
    await handleRefreshTraker()
  }

  const getAllUsers = async () => {
    try {
      const users = await getAllUsersByFilterHook({ hiddenUser: user })
      setUsersFilter(users)
    } catch (e) {
      console.error(e)
    }
  }

  const handleOpenEditTracker = ({ tracker }) => {
    setVisible(true)
    setSelectedTracker(tracker)
  }

  const handleSubmitHours = async ({
    resubmit = false,
    noneValidate,
    typeSubmit = TYPE_SUBMIT_ACTION.WEEK.id
  } = {}) => {
    if (!noneValidate && listTimeEntries.some((item) => item.is_running)) {
      return setErrorTracker('Please stop your timer before submitting for approval.')
    } else if (errorTracker) {
      setErrorTracker('')
    }

    let start_date = activeWeek[0].startOf('day')
    let end_date = activeWeek[6].endOf('day')

    if (typeSubmit !== TYPE_SUBMIT_ACTION.WEEK.id) {
      start_date = activeDate.startOf('day')
      end_date = activeDate.endOf('day')
    }

    const hereIsUserFilter = usersFilter.filter((item) => item.checked)?.[0]?.id

    const body = { user_id: hereIsUserFilter || user.id, start_date: start_date.toISO(), end_date: end_date.toISO() }

    const result = resubmit ? await reSubmitHoursTimeEntriesHook(body) : await submitHoursTimeEntriesHook(body)

    if (!result.success) {
      !noneValidate && setErrorTracker(result.message)
      return
    }

    Mixpanel.track(`Time Tracker - Submit ${typeSubmit} for approval`)

    !noneValidate && (await handleRefreshTraker())
  }

  const handleMoreDetailsApproval = () => {
    const listMessage = {}

    listTimeEntries.forEach((item) => {
      if (!listMessage[item.project_task.project.code]) {
        listMessage[item.project_task.project.code] = {
          project: `[${item.project_task.project.code}] ${item.project_task.project.name}`,
          message: item?.time_entry_detail?.comment || ''
        }
      }
    })

    setModalDetailsApproval({
      open: true,
      messages: listMessage
    })
  }

  // The approval status pill lives in the parent tab row (Tracker / Timesheet),
  // not in this page, so its state is reported up via onApprovalChange. Both
  // handlers are recreated every render (they close over listTimeEntries,
  // activeDate, etc.), so routing them through refs + a `useCallback([])`
  // wrapper keeps the *reported* onSubmit/onDetails referentially stable
  // while always calling the latest closure - reporting a fresh object on
  // every render (e.g. via an effect with no dependency array) would make
  // the parent's setState never bail out, which re-renders this component,
  // which reports again: an infinite loop.
  const handleSubmitHoursRef = useRef(handleSubmitHours)
  handleSubmitHoursRef.current = handleSubmitHours
  const handleMoreDetailsApprovalRef = useRef(handleMoreDetailsApproval)
  handleMoreDetailsApprovalRef.current = handleMoreDetailsApproval

  const stableOnSubmit = useCallback((...args) => handleSubmitHoursRef.current(...args), [])
  const stableOnDetails = useCallback((...args) => handleMoreDetailsApprovalRef.current(...args), [])

  useEffect(() => {
    onApprovalChange?.({ approval, onSubmit: stableOnSubmit, onDetails: stableOnDetails, calendarDrawerOpen })
  }, [approval, onApprovalChange, stableOnSubmit, stableOnDetails, calendarDrawerOpen])

  const validateAutoClose = async () => {
    const dayOfActiveWeek = DateTime.now().toFormat('cccc').toLowerCase()
    if (!scheduleHook?.data?.weekday || dayOfActiveWeek !== scheduleHook.data.weekday) return false

    const currentHours = DateTime.local()
    const scheduleHours = DateTime.local().set({ hour: scheduleHook.data.hours, minute: scheduleHook.data.minutes })
    const reSubmit = scheduleHours < currentHours

    if (!reSubmit) return false

    const trackerIsRunning = listTimeEntries.filter((item) => item.is_running)

    if (trackerIsRunning.length > 0) {
      await Promise.all(trackerIsRunning.map((item) => desActiveTimerTimeEntriesHook({ id: item.id })))
    }

    await handleSubmitHours({ resubmit: true, noneValidate: true })
    await handleSubmitHours({ noneValidate: true })
    await handleRefreshTraker()
    return true
  }

  const handleRunTime = async ({ run = true, idTimer }) => {
    const res = !run ? await validateAutoClose() : false

    if (res) return

    run ? await activeTimerTimeEntriesHook({ id: idTimer }) : await desActiveTimerTimeEntriesHook({ id: idTimer })
    await handleRefreshTraker()
  }

  const handleOpenAlert = () => setAlertTracker((prevState) => !prevState)

  useEffect(() => {
    const listPromise = [handleSetWeek({ currentDay: activeDate }), getScheduleHook(), getConnectedCalendarsHook()]

    if (!isDeveloper) listPromise.push(getAllUsers())

    Promise.allSettled(listPromise)
  }, [])

  useEffect(() => {
    // ? Only show the source filter once the user has at least one synced calendar - with nothing to filter against, it adds no value.
    if (connectedCalendarsHook.length === 0) return

    setSourceFilters(
      connectedCalendarsHook.map((calendar) => ({ id: calendar.id, text: calendar.summary, checked: true, color: calendar.color }))
    )

    // ? Connection status arrives asynchronously (in parallel with the initial week fetch), so re-fetch once it's known a synced calendar exists.
    if (activeWeek.length > 0) handleSetWeekTimeEntries({ activeWeek, currentDay: activeDate, filterUser: usersFilter })
  }, [connectedCalendarsHook])

  const filteredCalendarEventsPerDay = calendarEventsPerDay.filter(
    (event) => sourceFilters.find((source) => source.id === event.calendar_id)?.checked !== false
  )
  const pendingCalendarEventsCount = filteredCalendarEventsPerDay.filter((event) => !event.time_entry_id).length

  return (
    <>
      {loadingTimeEntriesHook && <Loading />}

      {errorTracker && (
        <div className="alert alert-warning text-center mt-4 mb-0" role="alert">
          {errorTracker}
        </div>
      )}
      <Helmet>
        <title>Time Tracker</title>
      </Helmet>

      {openTutorialContext && <OnboardingTooltip steps={ONBOARDING_STEPS_TRACKER} />}
      <div className="trackerWithCalendarDrawer">
        <div className="tracker mt-4">
          <div className="tracker_time mb-3">
            <div id="date_picker_tracker">
              <FilterTrackerByDate
                onChangeDay={handleChangeDay}
                currentDayName={currentDayName}
                activeDate={activeDate}
                usersFilter={usersFilter}
                onChangeUserFilters={handleChangeSwitches}
                pendingCalendarEventsCount={pendingCalendarEventsCount}
                onOpenCalendarDrawer={() => setCalendarDrawerOpen((isOpen) => !isOpen)}
                approval={approval}
                onOpenAlert={handleOpenAlert}
                selectedTracker={selectedTracker}
                onChangeSelectedTracker={setSelectedTracker}
                onOpen={setVisible}
                hoursActiveDay={listTimeEntries
                  .filter(
                    (item) =>
                      DateTime.fromISO(item.spent_date).setZone('local').toISODate() ===
                      activeDate.toFormat('yyyy-MM-dd')
                  )
                  .reduce((acc, item) => (acc = acc + item?.hours || 0), 0)}
              />
            </div>

            <div className="d-none d-sm-grid">
              <DaysOfWeek
                onChangeDay={handleChangeDay}
                listTimeEntries={listTimeEntries}
                activeWeek={activeWeek}
                currentDayName={currentDayName}
              />
            </div>
          </div>

          <div className="tracker_tickets">
            <ListTimeTicket
              timeEntriesPerDay={timeEntriesPerDay}
              onOpenEdit={handleOpenEditTracker}
              onRunTime={handleRunTime}
            />

            <div className="mt-4 d-block d-sm-none">
              <WeeklyTotalTracker listTimeEntries={listTimeEntries} />
            </div>

            <BtnSubmitTracker approval={approval} onSubmit={handleSubmitHours} listTimeEntries={listTimeEntries} />
          </div>
        </div>

        <CalendarEventsDrawer
          open={calendarDrawerOpen}
          setOpen={setCalendarDrawerOpen}
          currentDayName={`${currentDayName}, ${activeDate.toFormat('LLL dd')}`}
          hasConnectedCalendars={connectedCalendarsHook.length > 0}
          calendarFilters={sourceFilters}
          onChangeCalendarFilters={handleChangeSourceFilters}
          events={filteredCalendarEventsPerDay}
          onIncludeEvent={handleIncludeEvent}
          onExcludeEvent={handleExcludeEvent}
        />
      </div>

      {visible && (
        <TimeTrackerModal
          visible={visible}
          setVisible={() => setVisible(false)}
          date={activeDate}
          refreshTracker={handleRefreshTraker}
          tracker={selectedTracker}
          onDelete={() => setModalDeleteTracker(true)}
          selectedUser={getValuePerFilter({ list: usersFilter })}
          isOtherDay={activeDate.ordinal !== DateTime.now().ordinal}
        />
      )}

      {modalDeleteTracker && (
        <ModalDeleteTracker
          visible={modalDeleteTracker}
          setVisible={() => setModalDeleteTracker(false)}
          tracker={selectedTracker}
          onCancel={() => setVisible(true)}
          refreshTracker={handleRefreshTraker}
        />
      )}

      {modalDetailsApproval.open && (
        <ModalSuccess
          visible={modalDetailsApproval.open}
          setVisible={() => setModalDetailsApproval({ open: false })}
          title={<>Details of rejected time</>}
          content={
            <>
              {(Object.keys(modalDetailsApproval.messages) || []).map((item) => (
                <p key={modalDetailsApproval.messages[item].project}>
                  {modalDetailsApproval.messages[item].project}: {modalDetailsApproval.messages[item].message}
                </p>
              ))}
            </>
          }
          btnConfirm="Go back"
          Icon={ClockAfternoon}
        />
      )}

      {alertTracker && (
        <ModalSuccess
          visible={true}
          setVisible={handleOpenAlert}
          title="Please cancel your week submission before adding more time entries"
          Icon={WarningCircle}
          error
        />
      )}
    </>
  )
}

export default TimeTracker
